import { useMemo } from "react";
import { useFilteredNav } from "@theg1group/rbac-ui";
import { resolveIcon } from "./icons.js";
/**
 * Build sidebar groups from `FeatureModule.nav` declarations — no hardcoded
 * arrays. This is the extracted `buildGroups()` logic from the Console shell
 * (apps/admin/src/components/Layout.tsx), lifted here so every operator app
 * shares one renderer: bucket by `group`, sort items by `nav.order`, and order
 * groups by their minimum child `order`. Icon names are resolved to elements.
 */
export function buildNavGroups(features) {
    const byGroup = new Map();
    for (const f of features) {
        for (const n of f.nav) {
            const group = n.group ?? "";
            if (!byGroup.has(group))
                byGroup.set(group, []);
            byGroup.get(group)?.push({ label: n.label, path: n.path, order: n.order, icon: n.icon });
        }
    }
    const groups = [];
    for (const [label, entries] of byGroup.entries()) {
        entries.sort((a, b) => a.order - b.order);
        groups.push({
            label,
            minOrder: entries[0]?.order ?? Number.POSITIVE_INFINITY,
            entries,
        });
    }
    groups.sort((a, b) => a.minOrder - b.minOrder);
    return groups.map(({ label, entries }) => ({
        label: label || undefined,
        items: entries.map((e) => ({
            label: e.label,
            path: e.path,
            icon: resolveIcon(e.icon),
        })),
    }));
}
/**
 * Build the nav groups from feature modules and permission-filter them via
 * rbac-ui's `useFilteredNav` (route-resolved against pages_registry). Returns
 * groups ready to render in the shell sidebar — the single nav source every
 * operator app shares, replacing its hand-written `NAV[]`/`ITEMS[]` array.
 */
export function useShellNav(features) {
    const groups = useMemo(() => buildNavGroups(features), [features]);
    return useFilteredNav(groups);
}
//# sourceMappingURL=useShellNav.js.map